import { CalloutComponent, FormValues, getValue, InputFormCallout, InputFormGroup, TextInput } from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { parseId } from "../../helpers/idHelper";
import { validateMdsDescription } from "../../helpers/validationHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  MDS_UPDATE_POLL_DELAY,
  MDS_UPDATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

enum Fields {
  Description = "description",
}

export interface MysqlEditDescriptionProps extends OperationActionProps {
  databaseId: string | undefined;
  description: string | undefined;
}

const MysqlEditDescription = (
  { targetId, location, databaseId, description, onExecute, onCancel }: MysqlEditDescriptionProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName } = parseId(databaseId);
  const resourceGroupName = decodeURIComponent(resourceGroup || "");
  const dbSystemName = decodeURIComponent(resourceName || "");

  const { invokeCall: invokeMdsUpdate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).mdsDatabaseApi.updateMdsDbSystem },
  );
  const [calloutRef, setCalloutRef] = React.useState<CalloutComponent>({} as CalloutComponent);

  const onUpdate = (formValues: FormValues): void => {
    const descText = getValue(formValues, Fields.Description, InputFormGroup);
    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: calloutRef?.allowResubmit,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.updateMysqlDescription(),
          message: Messages.notifications.inProgress.messages.updateMysqlDescription(dbSystemName),
        },
        success: {
          title: Messages.notifications.success.titles.updateMysqlDescription(),
          message: Messages.notifications.success.messages.updateMysqlDescription(dbSystemName),
        },
        failure: {
          title: Messages.notifications.failure.titles.updateMysqlDescription(),
          message: Messages.notifications.failure.messages.updateMysqlDescription(dbSystemName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.MDS_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName,
            dbSystemName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.MDS_UPDATED_CHECK,
          delay: MDS_UPDATE_POLL_DELAY,
          interval: MDS_UPDATE_POLL_INTERVAL,
        },
      },
    };
    invokeMdsUpdate({
      dbSystemName,
      resourceGroupName,
      subscriptionId,
      updateMdsDbSystemDetails: { description: descText },
    }, invokeOptions);
  };

  return (
    <InputFormCallout
      targetId={targetId}
      title={Messages.createNewPanels.editDescription.title()}
      componentRef={setCalloutRef}
      onSubmit={onUpdate}
      onClose={onCancel}
    >
      <TextInput
        fieldName={Fields.Description}
        label={Messages.labels.description()}
        defaultValue={description}
        validator={validateMdsDescription}
      />
    </InputFormCallout>
  );
};

export const newMysqlEditDescription = (
  props: MysqlEditDescriptionProps,
): JSX.Element => (<MysqlEditDescription {...props} />);
